/**
 * Comprehensive list of LTL and Parcel carriers available in the US and Canada.
 * Used as the canonical dropdown source when connecting a carrier.
 */

export type CarrierDefinition = {
  name: string;
  code: string;
  type: "parcel" | "ltl" | "both";
};

export type CarrierCredentialFieldKey = "username" | "password" | "accountNumber";
export type CarrierCredentialAuthType = "login" | "oauth_client" | "api_key" | "shipengine_connected_carrier" | "managed_setup";

export type CarrierCredentialField = {
  key: CarrierCredentialFieldKey;
  label: string;
  required: boolean;
  placeholder?: string;
  type?: "text" | "password";
  helpText?: string;
};

export type CarrierCredentialRequirements = {
  authType: CarrierCredentialAuthType;
  selfService: boolean;
  fields: CarrierCredentialField[];
  notes: string;
  supportCta?: string;
};

const usernamePasswordAccountRequirements: CarrierCredentialRequirements = {
  authType: "login",
  selfService: true,
  fields: [
    { key: "username", label: "Username", required: true, placeholder: "carrier username" },
    { key: "password", label: "Password", required: true, placeholder: "carrier password", type: "password" },
    { key: "accountNumber", label: "Account Number", required: false, placeholder: "Optional account number" },
  ],
  notes: "Use the login credentials issued by the carrier. Some carriers also require an account number for rating, booking, or tracking.",
};

const oauthClientAccountRequirements: CarrierCredentialRequirements = {
  authType: "oauth_client",
  selfService: true,
  fields: [
    { key: "username", label: "Client ID", required: true, placeholder: "API client id" },
    { key: "password", label: "Client Secret", required: true, placeholder: "API client secret", type: "password" },
    { key: "accountNumber", label: "Shipper Account Number", required: true, placeholder: "Carrier account number" },
  ],
  notes: "Store the carrier OAuth/API client id as Client ID, client secret as Client Secret, and the shipper account number as the account number.",
};

const apiKeyRequirements: CarrierCredentialRequirements = {
  authType: "api_key",
  selfService: true,
  fields: [
    { key: "username", label: "API Key", required: true, placeholder: "carrier API key", type: "password" },
  ],
  notes: "Store the carrier API key/token. No carrier password is required for this integration model.",
};

const apiKeyAccountRequirements: CarrierCredentialRequirements = {
  authType: "api_key",
  selfService: true,
  fields: [
    { key: "username", label: "API Key", required: true, placeholder: "carrier API key", type: "password" },
    { key: "accountNumber", label: "Account Number", required: true, placeholder: "Carrier account number" },
  ],
  notes: "Store the carrier API key/token and carrier account number. No carrier password is required for this integration model.",
};

const shipEngineConnectedRequirements: CarrierCredentialRequirements = {
  authType: "shipengine_connected_carrier",
  selfService: true,
  fields: [
    { key: "username", label: "ShipEngine API Key", required: true, placeholder: "ShipEngine API key", type: "password" },
    { key: "accountNumber", label: "Connected Carrier ID", required: true, placeholder: "ShipEngine carrier_id" },
  ],
  notes: "Use the ShipEngine/ShipStation API key and the connected LTL carrier_id for this carrier account.",
};

const managedSetupRequirements: CarrierCredentialRequirements = {
  authType: "managed_setup",
  selfService: false,
  fields: [],
  notes: "This carrier requires ShipPeek support to confirm the usable API/EDI surface and onboarding steps before connection.",
  supportCta: "Request setup",
};

export const AVAILABLE_CARRIERS: CarrierDefinition[] = ([
  // ── Parcel carriers ──────────────────────────────────────────────
  { name: "UPS", code: "UPS", type: "both" },
  { name: "FedEx", code: "FEDEX", type: "both" },
  { name: "USPS", code: "USPS", type: "parcel" },
  { name: "DHL Express", code: "DHL_EXPRESS", type: "parcel" },
  { name: "DHL eCommerce", code: "DHL_ECOMMERCE", type: "parcel" },
  { name: "Canada Post", code: "CANADA_POST", type: "parcel" },
  { name: "Purolator", code: "PUROLATOR", type: "both" },
  { name: "Canpar", code: "CANPAR", type: "parcel" },
  { name: "GLS Canada", code: "GLS_CANADA", type: "parcel" },
  { name: "Loomis Express", code: "LOOMIS_EXPRESS", type: "parcel" },
  { name: "OnTrac", code: "ONTRAC", type: "parcel" },
  { name: "LaserShip", code: "LASERSHIP", type: "parcel" },
  { name: "Spee-Dee Delivery", code: "SPEEDEE", type: "parcel" },
  { name: "LSO (Lone Star Overnight)", code: "LSO", type: "parcel" },
  { name: "GSO (Golden State Overnight)", code: "GSO", type: "parcel" },
  { name: "Sendle", code: "SENDLE", type: "parcel" },
  { name: "Veho", code: "VEHO", type: "parcel" },
  { name: "CDL Last Mile", code: "CDL", type: "parcel" },
  { name: "Better Trucks", code: "BETTER_TRUCKS", type: "parcel" },
  { name: "Passport Shipping", code: "PASSPORT", type: "parcel" },
  { name: "Shippo", code: "SHIPPO", type: "parcel" },

  // ── LTL carriers ─────────────────────────────────────────────────
  { name: "AAA Cooper Transportation", code: "AAA_COOPER", type: "ltl" },
  { name: "ABF Freight", code: "ABF", type: "ltl" },
  { name: "ArcBest", code: "ARCBEST", type: "ltl" },
  { name: "Averitt Express", code: "AVERITT", type: "ltl" },
  { name: "Central Transport", code: "CENTRAL_TRANSPORT", type: "ltl" },
  { name: "Clear Lane Freight", code: "CLEAR_LANE", type: "ltl" },
  { name: "Con-way Freight", code: "CONWAY", type: "ltl" },
  { name: "Crossborder Xpress", code: "CROSSBORDER_XPRESS", type: "ltl" },
  { name: "Dayton Freight", code: "DAYTON", type: "ltl" },
  { name: "Dependable Highway Express", code: "DHE", type: "ltl" },
  { name: "Dohrn Transfer", code: "DOHRN", type: "ltl" },
  { name: "Dugan Truck Line", code: "DUGAN", type: "ltl" },
  { name: "Estes Express", code: "ESTES", type: "ltl" },
  { name: "Forward Air", code: "FORWARD_AIR", type: "ltl" },
  { name: "Frontline Freight", code: "FRONTLINE", type: "ltl" },
  { name: "Holland Motor Express", code: "HOLLAND", type: "ltl" },
  { name: "Kindersley Transport", code: "KINDERSLEY", type: "ltl" },
  { name: "Manitoulin Transport", code: "MANITOULIN", type: "ltl" },
  { name: "Midnite Express", code: "MIDNITE", type: "ltl" },
  { name: "New England Motor Freight", code: "NEMF", type: "ltl" },
  { name: "New Penn", code: "NEW_PENN", type: "ltl" },
  { name: "Oak Harbor Freight Lines", code: "OAK_HARBOR", type: "ltl" },
  { name: "Old Dominion Freight Line", code: "ODFL", type: "ltl" },
  { name: "Pitt Ohio", code: "PITT_OHIO", type: "ltl" },
  { name: "R+L Carriers", code: "RL_CARRIERS", type: "ltl" },
  { name: "Roadrunner Transportation", code: "ROADRUNNER", type: "ltl" },
  { name: "Saia", code: "SAIA", type: "ltl" },
  { name: "Southeastern Freight Lines", code: "SEFL", type: "ltl" },
  { name: "Standard Forwarding", code: "STANDARD_FORWARDING", type: "ltl" },
  { name: "TForce Freight", code: "TFORCE", type: "ltl" },
  { name: "USF Reddaway", code: "REDDAWAY", type: "ltl" },
  { name: "Ward Trucking", code: "WARD", type: "ltl" },
  { name: "Western Express", code: "WESTERN_EXPRESS", type: "ltl" },
  { name: "XPO Logistics", code: "XPO", type: "ltl" },
  { name: "YRC Freight", code: "YRC", type: "ltl" },

  // ── Canadian LTL carriers ────────────────────────────────────────
  { name: "Day & Ross", code: "DAY_ROSS", type: "ltl" },
  { name: "Vitran Express", code: "VITRAN", type: "ltl" },
  { name: "Midland Transport", code: "MIDLAND", type: "ltl" },
  { name: "TransX", code: "TRANSX", type: "ltl" },
  { name: "Polaris Transport", code: "POLARIS", type: "ltl" },
  { name: "Fastfrate", code: "FASTFRATE", type: "ltl" },
  { name: "Dicom Transportation", code: "DICOM", type: "ltl" },
  { name: "Bourret Transport", code: "BOURRET", type: "ltl" },
  { name: "H&R Transport", code: "HR_TRANSPORT", type: "ltl" },
] as const satisfies readonly CarrierDefinition[]).toSorted((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

const CARRIER_CREDENTIAL_REQUIREMENTS: Record<string, CarrierCredentialRequirements> = {
  ups: oauthClientAccountRequirements,
  fedex: oauthClientAccountRequirements,
  usps: {
    ...oauthClientAccountRequirements,
    fields: [
      { key: "username", label: "USPS Consumer Key", required: true, placeholder: "USPS Consumer Key" },
      { key: "password", label: "USPS Consumer Secret", required: true, placeholder: "USPS Consumer Secret", type: "password" },
      {
        key: "accountNumber",
        label: "USPS Payment Authorization Token",
        required: true,
        placeholder: "USPS payment authorization token",
        type: "password",
        helpText: "Required for live label booking. Shippeek uses the Consumer Key and Secret for server-to-server USPS OAuth.",
      },
    ],
    notes: "Use the customer's USPS Developer Portal credentials. They bring their own USPS app; Shippeek exchanges these credentials for USPS access tokens server-side.",
    supportCta: "Request USPS setup help",
  },
  dhl_express: oauthClientAccountRequirements,
  dhl_ecommerce: oauthClientAccountRequirements,
  shippo: apiKeyRequirements,
  sendle: apiKeyRequirements,
  passport: apiKeyRequirements,

  abf: shipEngineConnectedRequirements,
  arcbest: shipEngineConnectedRequirements,
  clear_lane: managedSetupRequirements,
  crossborder_xpress: managedSetupRequirements,
  frontline: managedSetupRequirements,
  western_express: managedSetupRequirements,
  kindersley: managedSetupRequirements,
  rl_carriers: apiKeyAccountRequirements,
  fastfrate: {
    ...usernamePasswordAccountRequirements,
    fields: usernamePasswordAccountRequirements.fields.filter((field) => field.key !== "accountNumber"),
    notes: "Use Fastfrate Tracker API username/password. No account number is required by default.",
  },
};

/** Quick lookup by carrier code */
export const CARRIER_BY_CODE = new Map(AVAILABLE_CARRIERS.map((c) => [c.code, c]));

export function getCarrierCredentialRequirements(carrierCode: string | null | undefined): CarrierCredentialRequirements {
  const normalized = String(carrierCode ?? "").trim().toLowerCase();
  return CARRIER_CREDENTIAL_REQUIREMENTS[normalized] ?? usernamePasswordAccountRequirements;
}

export function getCarrierCredentialField(
  carrierCode: string | null | undefined,
  key: CarrierCredentialFieldKey,
): CarrierCredentialField | null {
  return getCarrierCredentialRequirements(carrierCode).fields.find((field) => field.key === key) ?? null;
}

export function validateCarrierCredentialInput(
  carrierCode: string | null | undefined,
  input: Partial<Record<CarrierCredentialFieldKey, string | null | undefined>>,
): string | null {
  const requirements = getCarrierCredentialRequirements(carrierCode);
  if (!requirements.selfService) {
    return "This carrier requires ShipPeek-assisted setup. Please request setup instead of connecting it directly.";
  }

  for (const field of requirements.fields) {
    if (!field.required) continue;
    const value = String(input[field.key] ?? "").trim();
    if (!value) return `${field.label} is required for this carrier.`;
  }

  return null;
}
